import { useEffect, useState } from 'react';

import { usePromptPreview } from '../hooks/usePromptPreview';

interface PromptPreviewModalProps {
  slug: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal that renders the compiled prompt for `slug`. The user can override the
 * sample OCR text — the server re-renders with the provided sample on each
 * "Renderizar" press to avoid hammering the endpoint on every keystroke.
 */
export function PromptPreviewModal({ slug, open, onClose }: PromptPreviewModalProps): JSX.Element | null {
  const [sampleInput, setSampleInput] = useState<string>('');
  const [submittedSample, setSubmittedSample] = useState<string | undefined>(undefined);

  // Reset state every time the modal re-opens.
  useEffect(() => {
    if (open) {
      setSampleInput('');
      setSubmittedSample(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const { data, isLoading, isFetching, isError, error, refetch } = usePromptPreview(
    slug,
    submittedSample,
    { enabled: open },
  );

  if (!open) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-preview-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="card flex h-[80vh] w-full max-w-4xl flex-col p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="prompt-preview-title" className="text-lg font-semibold text-slate-900">
              Vista previa del prompt
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              El prompt que recibiría el proveedor de IA para el slug{' '}
              <code className="font-mono">{slug}</code>.
              {data !== undefined ? (
                <>
                  {' '}Versión <code className="font-mono">{data.version}</code> · ~
                  {data.estimatedInputTokens.toLocaleString('es-ES')} tokens estimados.
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Cerrar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <label htmlFor="prompt-sample" className="label">
            Texto OCR de ejemplo (opcional)
          </label>
          <textarea
            id="prompt-sample"
            className="input min-h-[5rem] font-mono text-xs"
            value={sampleInput}
            onChange={(event) => setSampleInput(event.target.value)}
            placeholder="Pega aquí un fragmento de OCR para ver cómo se inyectaría en el prompt."
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setSubmittedSample(sampleInput === '' ? undefined : sampleInput);
                void refetch();
              }}
              disabled={isFetching}
            >
              {isFetching ? 'Renderizando…' : 'Renderizar'}
            </button>
          </div>
        </div>

        <div className="mt-4 flex-1 overflow-auto rounded-md border border-slate-200 bg-slate-900">
          {isLoading ? (
            <div className="p-4 text-sm text-slate-300">Cargando prompt…</div>
          ) : isError ? (
            <div className="p-4 text-sm text-rose-300">
              No se pudo renderizar el prompt: {error?.message ?? 'error desconocido'}
            </div>
          ) : data === undefined ? (
            <div className="p-4 text-sm text-slate-300">Sin datos.</div>
          ) : (
            <pre className="whitespace-pre-wrap break-words p-4 text-xs text-slate-100">
              <code>{data.prompt}</code>
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
